/*
    Manage dialog windows
       creates a dialog window of several types and content
       prevent displaying several dialogs in the same time
       doing a postponed appearance
 */

import * as L from 'leaflet';

class UIDialogFactory {
    constructor(map, isMobileDevice) {
        this.map = map;
        this.isMobileDevice = isMobileDevice;
        this.currentDialog = null;
        this.infoWindows = {};
        this.dialogs = new Map(); // store active dialogs (as shown as hidden)
        this.onDialogOpen = []; // store handlers that calls when dialog is opened
        this.viewPortPadding = {
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
        }
        this.dialogsStack = [] // store dialogs stack to show
        this.viewPortPaddingFunc = null
    }

    setVieportPadding(padding) {
        if (typeof padding === "function") {
            this.viewPortPaddingFunc = padding
        } else {
            let p = this.viewPortPadding
            p.top = padding.top || p.top
            p.bottom = padding.bottom || p.bottom
            p.right = padding.right || p.right
            p.left = padding.left || p.left
        }
    }

    //create and show modal info window
    //options:
    //    title
    //    content
    //    onClose - callback function for Close button
    //    closeButtonName
    //    postpone  - the window can be postponed for N sec when another dialog window is already on the screen, default to 0 sec
    //    position  - "bottom", "bottomLeft", etc. default center
    modalInfoWindow(options) {
        let prompt = {
            buttonOK: "",
            buttonCancel: options.closeButtonName ? options.closeButtonName : "CLOSE",
        };

        if (typeof options.onClose === "function")
            prompt.cancel = options.onClose;

        let w = L.control.window(this.map, {
            className: "modalWindow control-window",
            title: options.title,
            content: options.content,
            closeButton: false,
            maxWidth: 350,
            modal: true,
            prompt: prompt,
            position: options.position,
        });
        if (this.currentDialog != null && options.postpone > 0) {
            this._dialogShowWithPostpone(w, null, options.postpone)
        } else {
            this._dialogShow(w, null);
        }
        return w;
    }

    // create and show info window
    //options:
    //    id
    //    title
    //    content
    //    onClose - callback function when window closed
    //    closeButtonName
    //    postpone  - the window can be postponed for N sec when another dialog window is already on the screen, default to 0 sec
    /**
     * creates and show info window usually for some user notification
     * @param options
     * @returns {*}
     */
    infoWindow(options) {
        let prompt = {
            buttonOK: "",
            buttonCancel: "",
        };

        // if this window is already on the screen
        if (this.infoWindows[options.id])
            return

        let w = L.control.window(this.map, {
            className: "infoWindow control-window",
            title: options.title,
            content: options.content,
            closeButton: true,
            maxWidth: 350,
            modal: false,
            prompt: prompt,
            position: (this.isMobileDevice ? "bottom" : "bottomLeft"),
        });

        w._wrapper.classList.add("lockcontrols");

        // in order the user would interact the map we are restoring the map events handlers disabled by the L.control.window
        var stop = L.DomEvent.stopPropagation;
        L.DomEvent
            .off(w._wrapper, 'mousedown', stop)
            .off(w._wrapper, 'touchstart', stop)
            .off(w._wrapper, 'dblclick', stop)
            .off(w._wrapper, 'mousewheel', stop)
            .off(w._wrapper, 'MozMousePixelScroll', stop);

        this.infoWindows[options.id] = w;
        w.on("hide close", function (e) {
            console.log(`Window '${e.sourceTarget.title()}' has been closed`);
            e.sourceTarget.off();
            delete this.infoWindows[options.id];
            if (typeof options.onClose === "function")
                options.onClose();

        }, this);
        w.show();
        console.log("Window '" + w.title() + "' has been shown");

        return w;
    }

    getInfoWindow(id) {
        return this.infoWindows[id]
    }

    getDialog(id) {
        return this.dialogs.get(id);
    }

    getCurrentDialog() {
        return this.currentDialog
    }
    /*
    options:
        title,
        content,
        yes          - callback for yes answer
        no           - callback for no answer
        invitation   - bool
        yesCaption,
        noCaption,
        timeout,
        postpone  - the window appearance can be postponed for N sec when another dialog window is already on the screen, default to 0 sec
     */
    /**
     * creates modl Yes/No window
     * @param options
     * @returns {*}
     */
    modalYesNoWindow(options) {
        let countdownTimer = null;
        let cancelButtonName = options.noCaption ? options.noCaption : "No";

        let w = L.control.window(this.map, {
            className: "modalWindow control-window" + (options.invitation ? " dialog-appear" : ""),
            title: options.title,
            content: options.content,
            closeButton: false,
            maxWidth: 350,
            modal: !options.invitation,
            prompt: {
                buttonOK: options.yesCaption ? options.yesCaption : "Yes",
                buttonCancel: cancelButtonName,
                callback: function () {
                    let timeout = false;
                    if (countdownTimer) {
                        countdownTimer.stop();
                        timeout = countdownTimer.isTimeout()
                    }
                    return options.yes(timeout)
                },
                cancel: function () {
                    let timeout = false;
                    if (countdownTimer) {
                        countdownTimer.stop();
                        timeout = countdownTimer.isTimeout()
                    }
                    if (options.no)
                        return options.no(timeout);
                    return true;
                },
                reverseOrder: true,
            },
            position: options.invitation ? (this.isMobileDevice ? "bottom" : "bottomLeft") : "",
        });

        if (options.timeout && options.timeout > 0) {
            countdownTimer = new CountdownTimer({
                timeout: options.timeout,
                text: cancelButtonName,
                prefix: "( ",
                suffix: " )",
                container: w._btnCancel,
                endCallback: function() {
                    w._container.dispatchEvent(new Event('L.Control.Window.Cancel'));
                }
            });
            w._btnCancel.innerText = countdownTimer.text();
        }
        if (this.currentDialog != null && options.postpone > 0) {
            this._dialogShowWithPostpone(w, countdownTimer, options.postpone)
        } else {
            this._dialogShow(w, countdownTimer);
        }
        return w;
    }

    /**
     * creates dialog window with content defined by UIComponent
     * @param UIC
     * @param options
     * @param parent
     */
    createUICDialog(UIC, options, parent) {
        let o = Object.assign({}, {
            className: "control-window " + UIC.className,
            title: UIC.title,
            content: UIC.initContent(),
            closeButton: false,
            maxWidth: 450,
            minWidth: 250,
            prompt: {
                buttonOK: UIC.cancelActionName,
                buttonAction: UIC.mainActionName,
                callback: UIC.onCancelAction.bind(UIC),
                action: UIC.onMainAction.bind(UIC)
            },
            canExtClosed: true,
            onExtClose: UIC.onCancelAction.bind(UIC),
            UIC: UIC
        }, options)
        let window = this._createDialogWindow(UIC.id, o);

        window.options.onExtClose = function () {
            // we do it so ass-backwards as prompt could be changed by UICs while dialog is opened
            // for example when many UIC is active on the dialog
            // so we need an actual UIC close handler to call on close
            window.options.prompt.callback()
        }
        // show dialog window
        if (this._dialogShow(window))
            // show ui component
            UIC.open(window, parent)
    }

    createDialog(name, options) {
        let window = this._createDialogWindow(name, options)
        // calls when dialog is closed from outside (by application or on other dialog appearance)
        window.options.onExtClose = function () {
            // we do it so ass-backwards because
            // the prompt with action callbacks could be changed
            // by UICs after the dialog was opened
            // so we need an actual callback
            window.options.prompt.callback()
        }
        this._dialogShow(window)
        return window
    }

    childCloseEvent(child, onCancel) {

    }

    /**
     * is called when any dialog is closed
     * @param e
     * @returns {boolean}
     * @private
     */
    _dialogCloseEvent(e) {
        if (e.type === 'hide') {
            console.log(`Dialog '${this._debugDialogTitle(e.sourceTarget)}' has been closed`);
        }
        if (e.type === 'close') {
            // disable all event handlers ?
            e.sourceTarget.off();
        }
        // ??
        if (this.currentDialog === e.sourceTarget) {
            // run handlers on dialog Open. why ???
            this._run(this.onDialogOpen, this.currentDialog)
            this.currentDialog = null;
        }
        return true
    }

    _createDialogWindow(name, options) {
        let dialog = this.dialogs.get(name);
        if (! dialog) {
            if (!options.padding) {
                options.padding = this.viewPortPaddingFunc ? this.viewPortPaddingFunc : this.viewPortPadding
            }
            dialog =  L.control.window(this.map, options);
            this.dialogs.set(name, dialog);
            dialog.on("close", function (ev) {
                this.dialogs.delete(name);
                if (options.onClose)
                    options.onClose()
            }, this);
        }
        dialog.parent = this
        return dialog;
    }

    addOnDialogOpenHandler(handler) {
        this.onDialogOpen.push(handler)
    }

    // calls when dialog is closed from outside (by application or on other dialog appearance)
    _closeCurrentByExtIfCan(initiator) {
        let d = this.currentDialog
        if (!d)
            return true

        if (!d.options.canExtClosed) {
            return false
        }

        if (typeof d.options.onExtClose === "function") {
            d.options.onExtClose()
        }
        // close the dialog if it is not the initiator
        // it can happen when this dialog is just reopened with other data
        if (d !== initiator) {
            // this.currentDialog can be closed by external component from d.options.onExtClose(), so check it
            if (this.currentDialog)
                this.currentDialog.close()
        }
        return true
    }

    _dialogShow(dialog, countdownTimer) {
        // if we have another dialog on the screen and it can be closed, close it
        if (!this._closeCurrentByExtIfCan(dialog))
            return false

        // run handlers
        this._run(this.onDialogOpen, dialog)
        this.currentDialog = dialog;
        dialog.on("hide close", this._dialogCloseEvent, this);
        dialog.show();
        console.log(`Dialog '${this._debugDialogTitle(dialog)}' has been shown`);
        if (countdownTimer)
            countdownTimer.start();
        return true
    }

    _dialogShowWithPostpone(dialog, countdownTimer, postpone) {
        let title =  dialog.title();
        console.log(`The dialog window '${title}' has been postponed for ${postpone} seconds (max) because of another dialog is already on the screen`);
        // todo CountdownTimer is not implemented (is lost from locator)
        let waitingCountDown = new CountdownTimer({
            timeout: postpone,
            tickCallback: function (timer) {
                if (this.currentDialog == null) {
                    this._dialogShow(dialog, countdownTimer);
                    return true;
                }
                return false;
            }.bind(this),
            endCallback: function () {
                this._dialogShow(dialog, countdownTimer);
            }.bind(this)
        }).start();
    }

    _run(list, data) {
        for (let c of list) {
            if (typeof c === "function")
                c(data)
        }
    }

    _debugDialogTitle(dialog) {
        let title =  dialog.title();
        if (title === '') {
            this.dialogs.forEach(function (v, k) {
                if (v === dialog)
                    title = k
            })
        }
        return title
    }
}

export {UIDialogFactory};

